import {useContext} from "react";
import {useLocation} from "react-router-dom";
import {UserContext} from "../App";
import Header2 from "./Header2";
import {Box, Typography} from "@mui/material";
import MuiLink from "@mui/material/Link";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// First-line-of-defense route guard. If the current path matches a SECURED catalog
// page (delivered on the user context as `securedPages`) and the user lacks the
// matching ('page') grant, show an access message instead of the page. This is a UX
// layer only — the API routes still enforce access server-side.
const HELP_EMAIL = "help@usalco.com";

// Drop a trailing slash (but keep root "/") so '/inventory_count/' matches '/inventory_count'.
const norm = (s) => (typeof s === "string" && s.length > 1 ? s.replace(/\/+$/, "") : s);

// Does a catalog page url pattern match the current path? ':param' segments are wildcards.
const matchUrl = (pattern, path) => {
	if (typeof pattern !== "string" || typeof path !== "string") return false;
	const p = norm(pattern);
	const t = norm(path);
	if (!p.includes(":")) return p.toLowerCase() === t.toLowerCase();
	const rx = new RegExp(
		"^" + p.split("/").map((seg) =>
			seg.startsWith(":") ? "[^/]+" : seg.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
		).join("/") + "$",
		"i"
	);
	return rx.test(t);
};

const hasPageGrant = (securities, guid) =>
	Array.isArray(securities) && !!guid && securities.some((s) =>
		s && s.ua_type === "page" && typeof s.ua_fid === "string" &&
		s.ua_fid.toUpperCase() === guid.toUpperCase()
	);

function AccessDenied({pageName}) {
	return (
		<Header2 pageName="Access Denied">
			<Box sx={{height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 3, boxSizing: "border-box"}}>
				<Box sx={{maxWidth: 480, textAlign: "center"}}>
					<LockOutlinedIcon sx={{fontSize: 56, color: "text.disabled", mb: 1.5}}/>
					<Typography variant="h5" sx={{mb: 1}}>Access restricted</Typography>
					<Typography color="text.secondary" sx={{mb: 2}}>
						You don't have access to {pageName ? <>the <strong>{pageName}</strong></> : "this"} page in The Reservoir.
					</Typography>
					<Typography color="text.secondary">
						If you believe this is a mistake, ask your supervisor to request access for you at{" "}
						<MuiLink href={`mailto:${HELP_EMAIL}?subject=${encodeURIComponent("Reservoir access request")}`}>
							{HELP_EMAIL}
						</MuiLink>.
					</Typography>
				</Box>
			</Box>
		</Header2>
	);
}

export default function PageGuard({children}) {
	const user = useContext(UserContext);
	const location = useLocation();

	const securedPages = user && Array.isArray(user.securedPages) ? user.securedPages : [];
	const securities = user ? user.securities : [];

	const matched = securedPages.find((p) => matchUrl(p.url, location.pathname));
	if (matched && !hasPageGrant(securities, matched.guid)) {
		return <AccessDenied pageName={matched.name}/>;
	}
	return children;
}
